import { useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import { toast } from "sonner";
import { CircleCheck, Wallet, CheckCircle2 } from "lucide-react";
import Loading from "../../../cors/Loading";
import { useLanguage } from "../../../context/LanguageProvider";

export default function Page() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [payMode, setPayMode] = useState('full');
  const [partialAmount, setPartialAmount] = useState('');

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`${BASE_URL}/api/payment-system`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      });

      const result = await response.json();
      setData(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const handlePay = async () => {
    if (!data || data.amount_due <= 0) return;

    const amount = payMode === 'partial' ? parseFloat(partialAmount) : data.amount_due;

    if (payMode === 'partial' && (!amount || amount <= 0 || amount > data.amount_due)) {
      toast.error(t('payments.system.invalid_amount'));
      return;
    }

    setIsPaying(true);
    try {
      const response = await fetch(`${BASE_URL}/api/payment-system`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount })
      });

      const result = await response.json();

      if (!result.success) {
        toast.error(result.message || t('payments.system.update_failed'));
        return;
      }

      fetchData();
      toast.success(t('payments.system.paid_success'));
    } catch (err) {
      toast.error(t('payments.system.update_failed'));
    } finally {
      setIsPaying(false);
      setShowModal(false);
      setPayMode('full');
      setPartialAmount('');
    }
  };

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <>
      {showModal && data && data.amount_due > 0 && (
        <div className="fixed inset-0 z-[99] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
              onClick={() => !isPaying && setShowModal(false)}
            />

            <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
              <div className="flex items-center justify-center mb-4">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                  <svg
                    className="h-6 w-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-sm font-semibold leading-6 text-gray-900 mb-2">
                  {t('payments.system.confirm_payment')}
                </h3>
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-4">
                    {t('payments.system.confirm_message')}
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-xs text-gray-600 mb-1">{t('payments.system.amount_label')}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {parseFloat(data.amount_due).toFixed(2)} MAD
                    </p>
                  </div>

                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      className={`flex-1 cursor-pointer py-2 px-3 rounded-md text-xs font-medium border transition-all ${
                        payMode === 'full'
                          ? 'bg-green-700 text-white border-green-700'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => { setPayMode('full'); setPartialAmount(''); }}
                    >
                      {t('payments.system.pay_full')}
                    </button>
                    <button
                      type="button"
                      className={`flex-1 cursor-pointer py-2 px-3 rounded-md text-xs font-medium border transition-all ${
                        payMode === 'partial'
                          ? 'bg-green-700 text-white border-green-700'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setPayMode('partial')}
                    >
                      {t('payments.system.pay_partial')}
                    </button>
                  </div>

                  {payMode === 'partial' && (
                    <div className="mb-4">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={data.amount_due}
                        placeholder={t('payments.system.enter_amount')}
                        value={partialAmount}
                        onChange={(e) => setPartialAmount(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  className="inline-flex w-full cursor-pointer justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => { setShowModal(false); setPayMode('full'); setPartialAmount(''); }}
                  disabled={isPaying}
                >
                  {t('payments.system.cancel')}
                </button>
                <button
                  type="button"
                  className="inline-flex w-full cursor-pointer justify-center rounded-md border border-transparent bg-green-700 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-green-800 disabled:opacity-50"
                  onClick={handlePay}
                  disabled={isPaying}
                >
                  {isPaying ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t('payments.system.confirm_button')}
                    </span>
                  ) : (
                    t('payments.system.confirm_button')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12 h-[91vh]">
        <div className="mb-10">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-2">
                {t('payments.system.title')}
              </h1>
              <p className="text-sm text-gray-600">
                {t('payments.system.subtitle')}
              </p>
            </div>
            <button
              onClick={fetchData}
              className="flex cursor-pointer items-center gap-2 text-xs py-2 px-4 bg-[var(--color-primary-500)] text-white rounded-lg hover:bg-[var(--color-primary-400)] transition-colors duration-300"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t('payments.system.refresh')}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="w-[100%] flex justify-center pt-40">
            <Loading />
          </div>
        ) : data ? (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
              <div className="flex justify-between items-start mb-6">
                <div className="bg-red-100 p-3 max-w-max rounded-md">
                  <Wallet className="w-6 h-6 text-red-600" />
                </div>
                {data.amount_due > 0 ? (
                  <span className="px-3 py-1 text-xs font-semibold rounded-full border border-rose-100 bg-rose-50 text-rose-700 group-hover:bg-rose-100">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-rose-500 rounded-full"></div>
                      {t('payments.system.unpaid')}
                    </div>
                  </span>
                ) : (
                  <span className="px-3 py-1 text-xs font-semibold rounded-full border border-emerald-100 bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100">
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                      {t('payments.system.paid_status')}
                    </div>
                  </span>
                )}
              </div>

              <div className="text-center mb-6">
                <p className="text-xs text-gray-500 mb-2">{t('payments.system.amount_label')}</p>
                <p className={`text-4xl font-bold ${data.amount_due > 0 ? 'text-emerald-600' : 'text-green-600'}`}>
                  {new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(data.amount_due)} MAD
                </p>
              </div>

              {data.amount_due > 0 && (
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full cursor-pointer py-3 px-4 rounded-md bg-green-700 text-white font-semibold hover:shadow-lg hover:shadow-emerald-100 hover:bg-green-800 transition-all duration-300"
                >
                  <span className="flex items-center text-sm justify-center gap-2">
                    <CircleCheck className="w-4 h-4 text-white" />
                    {t('payments.system.pay_button')}
                  </span>
                </button>
              )}

              {data.amount_due <= 0 && (
                <div className="w-full py-3 rounded-xl bg-gray-50 text-center">
                  <p className="text-sm font-medium text-gray-600 flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    {t('payments.system.payment_completed')}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center pt-32">
            <div className="w-32 h-32 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-12 h-12 text-[var(--color-primary-500)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xs text-gray-700 mb-2">
              {t('payments.system.no_payments')}
            </h3>
          </div>
        )}
      </div>
    </>
  );
}
